import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectHexes,
  selectPendingMeleeDamage,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calculateMeleeCombatDamage} from "../../../redux/actions";
import {calcMeleeCombatInput} from "../../gameState/melee/calcMeleeCombatInput";
import {calcMeleeDamage} from "../../damage/calcMeleeDamage";
import {getNeighborEnemyUnits} from "../../unit/getNeighborEnemyUnits";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {getDamageDiceRoll} from "../../dice/getDamageDiceRoll";
import {getBotCombatD6MinRoll} from "../../dice/getBotCombatD6MinRoll";
import {getBotD6RollBonus} from "../../dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../../dice/getLowRatingAdvantageDicePenalty";
import {BOT_PRESSURE_TARGET_WEIGHT} from "../../../configs/botPressure";
import {getPlayersByIds} from "../../players/getPlayersByIds";
import {checkIfAlreadyBestMeleeCombatDamage} from "../../melee/checkIfAlreadyBestMeleeCombatDamage";
import {calcEnemyBlockedAssetValues} from "../evaluation/blockedAssets";
import {getBotGridRenderer} from "../botGrid";
import {checkIfBotHasDiceBonus, checkIfBotShouldRerollDamageDie} from "./checkIfBotShouldRerollDamageDie";
import {selectBotRerollDice} from "./selectBotRerollDice";
import {createBotMeleeValueGetter} from "./createBotMeleeValueGetter";
import {getMeleeCountedDiceRolls, getMeleeDiceRolls} from "../../melee/getMeleeDiceRolls";
import {MELEE_COUNTED_DAMAGE_DICE_COUNT} from "../../../configs/damage";
import {isPreW42C2Player} from "../abTestGates";
import {selectCelectialsRerollDice} from "../factions/celectialsBot";

// Множитель ценности блокируемого актива при выборе приоритетной цели:
// убийство блокера освобождает точку/строение на все оставшиеся раунды
const PRIORITY_TARGET_BLOCK_FACTOR = 2;

// Отслеживание уже расставленных приоритетов в рамках текущего боя,
// чтобы не переназначать цели бесконечно
let handledCombatKey = null;
let handledUnitIds = new Set();

/**
 * Сброс модульного состояния ближнего боя. Обязателен вокруг песочницы
 * «сильного режима»: песочница зеркалит реальные раунды/id игроков/отрядов,
 * и ее бои иначе отравляют трекинг реальных боев с теми же ключами
 * (реальный бой пропускал бы приоритетные цели как «уже обработанные»).
 */
export const resetMeleeHandlingTracking = () => {
  handledCombatKey = null;
  handledUnitIds = new Set();
};

/**
 * Суммарный распределенный урон по каждому отряду от ВСЕХ участников боя.
 * Нужен для честной проверки "убийство уже обеспечено" перед перебросом.
 */
const calcTotalDamageByUnitId = (pendingMeleeDamage) => {
  const totalDamageByUnitId = {};
  pendingMeleeDamage.forEach((entry) => {
    Object.entries(entry.damageDistribution || {}).forEach(([targetUnitId, damage]) => {
      totalDamageByUnitId[targetUnitId] = (totalDamageByUnitId[targetUnitId] || 0) + damage;
    });
  });
  return totalDamageByUnitId;
};

/**
 * Переброс плохих костей ближнего боя за дополнительное снабжение
 * (точное повторение диспатча SelectedUnitMelee.handleRerollClick).
 * Работает для любого бота-участника, не только текущего игрока.
 * Возвращает true, если переброс сделан.
 */
const tryAdditionalSupplyReroll = (
  store, state, combatOwnerId, botPlayer, playerUnits, pendingMeleeDamage, damageContext, totalDamageByUnitId
) => {
  for (const unit of playerUnits) {
    if (!unit.additionalSupply) {
      continue;
    }
    const meleeEntry = pendingMeleeDamage.find((action) => action.unitId === unit.id);
    if (!meleeEntry || meleeEntry.hasRerolled) {
      continue;
    }
    // Отряд с 4 костями (селестиалы, в том числе соблазненный): урон считает лучшие две
    const diceRolls = getMeleeDiceRolls(meleeEntry);
    const isManyDice = diceRolls.length > MELEE_COUNTED_DAMAGE_DICE_COUNT;
    if (isManyDice && isPreW42C2Player(botPlayer.id)) {
      continue;
    }
    const [diceRoll1, diceRoll2] = getMeleeCountedDiceRolls(meleeEntry);
    if (checkIfAlreadyBestMeleeCombatDamage(diceRoll1, diceRoll2)) {
      continue;
    }

    // Противники именно в ЭТОМ бою — те, между кем распределяется урон отряда.
    // Соседи вне боя (нейтральные или третьего игрока на чужом ходу) урона не
    // получают и в расчет не идут
    const combatEnemies = selectUnits(state).filter(
      (enemy) => Object.prototype.hasOwnProperty.call(meleeEntry.damageDistribution || {}, enemy.id)
    );

    // Переброс собственных костей бота: подкрутка d6 его уровнем бонуса (см.
    // getBotD6MinRoll, getBotD6RollBonus; против бота ее нет) плюс +1 к
    // полу, если отряд зажат игроком под давлением ботов
    // (см. getBotCombatD6MinRoll). От этих же подкруток зависит, какие кости
    // вообще стоит перебрасывать
    const combatEnemyOwners = getPlayersByIds(
      selectPlayers(state), combatEnemies.map((enemy) => enemy.ownerId)
    );
    const botMinRoll = getBotCombatD6MinRoll(botPlayer, combatEnemyOwners);
    const botRollBonus = getBotD6RollBonus(botPlayer, combatEnemyOwners);
    // Фора новичкам: отряд, зажатый игроком с форой, и перебрасывает со
    // штрафом к грани — иначе переброс обходил бы штраф (см.
    // getLowRatingAdvantageDicePenalty)
    const botFacePenalty = getLowRatingAdvantageDicePenalty(botPlayer, combatEnemyOwners);

    // С подкрутками — по средней пользе переброса для всего боя: верное
    // уничтожение противников она видит сама (выигрыша нет), в том числе у
    // орков и в крепости, где урон не равен потере численности
    const getValue = checkIfBotHasDiceBonus(botMinRoll, botRollBonus)
      ? createBotMeleeValueGetter({
        sharedState: state.shared, unit, botPlayerId: botPlayer.id, combatOwnerId,
      })
      : null;

    let selection;
    if (isManyDice) {
      const isAllKillsSecured = combatEnemies.length > 0 && combatEnemies.every(
        (enemy) => (totalDamageByUnitId[enemy.id] || 0) >= enemy.size
      );
      if (isAllKillsSecured && !getValue) {
        continue;
      }
      const flags = selectCelectialsRerollDice({
        rolls: diceRolls, minRoll: botMinRoll, rollBonus: botRollBonus, facePenalty: botFacePenalty,
        getValue: getValue || undefined,
      });
      if (!flags.some(Boolean)) {
        continue;
      }
      const newDiceEntry = Object.fromEntries(diceRolls.map((diceRoll, index) => [
        `diceRoll${index + 1}`,
        flags[index] ? getDamageDiceRoll(botMinRoll, botRollBonus, botFacePenalty) : diceRoll,
      ]));
      const manyDiceCombatInput = calcMeleeCombatInput(
        selectUnits(state), combatOwnerId, pendingMeleeDamage, {[unit.id]: newDiceEntry}, selectPlayers(state)
      );
      const {damage: prevManyDiceDamage} = calcMeleeDamage(unit, diceRoll1, diceRoll2, damageContext);
      const {damage: newManyDiceDamage} = calcMeleeDamage(
        unit, ...getMeleeCountedDiceRolls(newDiceEntry), damageContext
      );
      store.dispatch(calculateMeleeCombatDamage(
        manyDiceCombatInput, unit.id, undefined, true, newManyDiceDamage <= prevManyDiceDamage, ...flags
      ));
      return true;
    }
    if (getValue) {
      selection = selectBotRerollDice({
        diceRoll1, diceRoll2, minRoll: botMinRoll, rollBonus: botRollBonus, facePenalty: botFacePenalty, getValue,
      });
    } else {
      // Без подкруток (или если бой не просчитать) — по самой кости. Все
      // противники в бою уже уничтожаются СУММАРНЫМ распределенным уроном
      // участников — переброс ничего не добавит, снабжение не тратим
      const isAllKillsSecured = combatEnemies.length > 0 && combatEnemies.every(
        (enemy) => (totalDamageByUnitId[enemy.id] || 0) >= enemy.size
      );
      if (isAllKillsSecured) {
        continue;
      }
      selection = {
        isDice1Selected: checkIfBotShouldRerollDamageDie(diceRoll1, botMinRoll, botRollBonus, botFacePenalty),
        isDice2Selected: checkIfBotShouldRerollDamageDie(diceRoll2, botMinRoll, botRollBonus, botFacePenalty),
      };
    }
    const {isDice1Selected, isDice2Selected} = selection;
    if (!isDice1Selected && !isDice2Selected) {
      continue;
    }

    const newDiceRoll1 = isDice1Selected ? getDamageDiceRoll(botMinRoll, botRollBonus, botFacePenalty) : diceRoll1;
    const newDiceRoll2 = isDice2Selected ? getDamageDiceRoll(botMinRoll, botRollBonus, botFacePenalty) : diceRoll2;
    const damageToChange = {
      [unit.id]: {
        diceRoll1: newDiceRoll1,
        diceRoll2: newDiceRoll2,
      },
    };
    const meleeCombatInput = calcMeleeCombatInput(
      selectUnits(state), combatOwnerId, pendingMeleeDamage, damageToChange, selectPlayers(state)
    );

    const {damage: prevDamage} = calcMeleeDamage(unit, diceRoll1, diceRoll2, damageContext);
    const {damage: newDamage} = calcMeleeDamage(unit, newDiceRoll1, newDiceRoll2, damageContext);
    const isBadLuck = newDamage <= prevDamage;

    store.dispatch(calculateMeleeCombatDamage(
      meleeCombatInput, unit.id, undefined, true, isBadLuck, isDice1Selected, isDice2Selected
    ));
    return true;
  }

  return false;
};

/**
 * Этап ближнего боя фазы войны: перебросы и приоритетные цели для ВСЕХ ботов,
 * чьи отряды участвуют в состоявшихся боях (текущий игрок и защитники).
 * Урон уже рассчитан (кости брошены при входе в этап).
 * По умолчанию игра бьет наименьший смежный отряд; бот выбирает цель с
 * наибольшей суммой "размер + ценность блокируемых им наших активов".
 */
export const decideCombatStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const players = selectPlayers(state);
  const round = selectRound(state);
  const units = selectUnits(state);
  const pendingMeleeDamage = selectPendingMeleeDamage(state);

  const combatKey = `${round}_${currentPlayer.id}`;
  if (handledCombatKey !== combatKey) {
    handledCombatKey = combatKey;
    handledUnitIds = new Set();
  }

  if (pendingMeleeDamage.length === 0) {
    return false;
  }

  const participantOwnerIds = new Set(pendingMeleeDamage.map((action) => action.ownerId));
  const botParticipants = players.filter(
    (player) => player.isBot && participantOwnerIds.has(player.id)
  );
  if (botParticipants.length === 0) {
    return false;
  }

  // Противники под давлением ботов (см. applyBotPressureLatches): их отряды —
  // более ценная приоритетная цель. В нерейтинговых партиях набор пуст
  const pressuredEnemyPlayerIds = new Set(
    players.filter((player) => player.isUnderBotFocus).map((player) => player.id)
  );

  const totalDamageByUnitId = calcTotalDamageByUnitId(pendingMeleeDamage);
  const gridRenderer = getBotGridRenderer(state);
  const citiesCache = selectCitiesCache(state);
  const sharedContext = {
    citiesCache,
    specialBuildings: selectSpecialBuildings(state),
    buildingsDamage: selectBuildingsDamage(state),
    hexes: selectHexes(state),
    units,
    players,
  };

  for (const botPlayer of botParticipants) {
    const damageContext = {...sharedContext, owner: botPlayer};
    const playerUnits = getPlayerUnits(botPlayer, units);

    // Сначала перебрасываем плохие кости за дополнительное снабжение — переброс
    // меняет урон, приоритетные цели назначаем уже по итоговым значениям
    if (tryAdditionalSupplyReroll(
      store, state, currentPlayer.id, botPlayer, playerUnits, pendingMeleeDamage, damageContext, totalDamageByUnitId
    )) {
      return true;
    }

    // Отряды противника, блокирующие НАШИ точки/строения — приоритетные цели
    const blockValues = calcEnemyBlockedAssetValues(
      botPlayer, units, citiesCache, sharedContext.specialBuildings, gridRenderer
    );

    for (const unit of playerUnits) {
      if (handledUnitIds.has(unit.id)) {
        continue;
      }

      const meleeEntry = pendingMeleeDamage.find((action) => action.unitId === unit.id);
      if (!meleeEntry) {
        continue;
      }

      const adjacentEnemies = getNeighborEnemyUnits(unit.ownerId, unit.hexKey, units);
      if (adjacentEnemies.length < 2) {
        continue; // с единственной целью приоритет не нужен
      }

      const [countedDiceRoll1, countedDiceRoll2] = getMeleeCountedDiceRolls(meleeEntry);
      const {damage} = calcMeleeDamage(unit, countedDiceRoll1, countedDiceRoll2, damageContext);
      if (damage <= 0) {
        continue;
      }

      const killableEnemies = adjacentEnemies.filter((enemy) => enemy.size <= damage);
      if (killableEnemies.length === 0) {
        continue;
      }

      // По умолчанию игра выберет наименьший отряд — он и так убиваем.
      // Переназначаем только если находим более ценную убиваемую цель:
      // размер + ценность блокируемых ею наших активов
      // Давление ботов: отряд игрока, против которого боты играют сообща,
      // ценнее как приоритетная цель (см. src/configs/botPressure.js)
      const targetScore = (enemy) =>
        (enemy.size + (blockValues.get(enemy.id) || 0) * PRIORITY_TARGET_BLOCK_FACTOR)
        * (pressuredEnemyPlayerIds.has(enemy.ownerId) ? BOT_PRESSURE_TARGET_WEIGHT : 1);
      const smallestEnemy = adjacentEnemies.reduce((min, enemy) => (enemy.size < min.size ? enemy : min));
      const bestKill = killableEnemies.reduce(
        (best, enemy) => (targetScore(enemy) > targetScore(best) ? enemy : best)
      );
      if (bestKill.id === smallestEnemy.id) {
        handledUnitIds.add(unit.id);
        continue;
      }

      handledUnitIds.add(unit.id);

      const meleeCombatInput = calcMeleeCombatInput(units, currentPlayer.id, pendingMeleeDamage, {}, players);
      store.dispatch(calculateMeleeCombatDamage(meleeCombatInput, undefined, {[unit.id]: bestKill.id}));
      return true;
    }
  }

  return false;
};
